/**
 * Icon resolution for the public link page and for the settings dialog that feeds it.
 *
 * A link button shows, in order of preference: the icon the studio chose, the emoji it typed at
 * the start of the button's name, or the first letter of that name. That order is why nothing
 * here throws — every step has a cheaper fallback behind it.
 */

const FAVICON_SERVICE_URL = "https://www.google.com/s2/favicons";
const FAVICON_SIZE = 128;

/**
 * The app's own icon, served from `public/`. Root-relative on purpose: the link page runs on the
 * studio's own domain, so this resolves there and keeps working if the studio changes domains.
 */
export const PLATFORM_ICON_PATH = "/icon-192.png";

// Matches a pictographic character plus whatever modifies it (skin tone, variation selector,
// zero-width joiner sequences), so "👩‍🦰 Екип" yields the whole emoji rather than half of it.
const LEADING_EMOJI_PATTERN =
  /^(\p{Extended_Pictographic}(️|\p{Emoji_Modifier}|‍\p{Extended_Pictographic}(️)?)*)/u;

// Anything that is not a letter or a digit — used to strip emojis and punctuation before
// picking the letters for a monogram.
const NON_ALPHANUMERIC_PATTERN = /[^\p{L}\p{N}]/gu;

/**
 * Parses whatever the studio typed into a URL. Accepts a bare domain, because the dialog has to
 * suggest an icon before the backend gets a chance to prepend the scheme on save.
 */
const parseHost = (rawUrl: string): string | null => {
  const value = (rawUrl ?? "").trim();

  if (!value) {
    return null;
  }

  const candidate = /^https?:\/\//i.test(value) ? value : `https://${value}`;

  try {
    const { hostname, protocol } = new URL(candidate);

    // A "javascript:..." string survives the prefixing above as a host of sorts, so the
    // protocol is checked rather than assumed.
    if (protocol !== "http:" && protocol !== "https:" || !hostname) {
      return null;
    }

    return hostname;
  } catch {
    return null;
  }
};

/** Drops a leading "www." so "www.studio.com" and "studio.com" count as the same host. */
const normalizeHost = (host: string) => host.toLowerCase().replace(/^www\./, "");

/**
 * Whether the link points back at the studio's own site. `ownDomains` is the studio's `url`
 * field, which holds one or more comma-separated domains.
 */
const isOwnDomain = (host: string, ownDomains?: string): boolean => {
  if (!ownDomains?.trim()) {
    return false;
  }

  const target = normalizeHost(host);

  return ownDomains
    .split(",")
    .map((domain) => parseHost(domain))
    // Whole-host equality, not endsWith: "studio.com.evil.com" ends with the studio's domain
    // but belongs to someone else.
    .some((candidate) => !!candidate && normalizeHost(candidate) === target);
};

/**
 * Suggests an icon for a link.
 *
 * Google's favicon service answers from its own crawl cache rather than fetching the site, so a
 * private per-studio subdomain it has never crawled comes back as a generic globe — with a 200,
 * which means no error handler downstream can tell it went wrong. Links back to the studio's own
 * site therefore use the app's bundled icon instead of asking at all.
 *
 * Returns an empty string when the input is not a usable address, so the caller can just clear
 * the field.
 */
export const deriveIconUrlFromLink = (
  rawUrl: string,
  ownDomains?: string,
): string => {
  const host = parseHost(rawUrl);

  if (!host) {
    return "";
  }

  if (isOwnDomain(host, ownDomains)) {
    return PLATFORM_ICON_PATH;
  }

  return `${FAVICON_SERVICE_URL}?domain=${encodeURIComponent(host)}&sz=${FAVICON_SIZE}`;
};

/** Returns the emoji a button's name starts with, or null when it starts with anything else. */
export const getLeadingEmoji = (text: string): string | null => {
  const match = (text ?? "").trim().match(LEADING_EMOJI_PATTERN);

  return match ? match[0] : null;
};

/**
 * Initials for the studio's name or a button's label, used wherever there is no image to show.
 * Emojis and punctuation are skipped, so "✨ Tiktok" gives "T" rather than the sparkles.
 */
export const getMonogram = (text: string, letters = 1): string => {
  const words = (text ?? "")
    .split(/\s+/)
    .map((word) => word.replace(NON_ALPHANUMERIC_PATTERN, ""))
    .filter(Boolean);

  return words
    .slice(0, letters)
    .map((word) => word[0].toUpperCase())
    .join("");
};

interface IResolveSuggestedIconParams {
  newUrl: string;
  currentIcon: string;
  lastSuggestion: string;
  /** The studio's own domains, comma-separated, as stored on its `url` field. */
  ownDomains?: string;
}

/**
 * Decides what the icon field should hold after the link changed.
 *
 * The field is auto-filled, but it is also editable — so a value the studio typed itself is
 * left alone forever. Only an empty field, or one still holding the previous suggestion, gets
 * overwritten.
 */
export const resolveSuggestedIcon = ({
  newUrl,
  currentIcon,
  lastSuggestion,
  ownDomains,
}: IResolveSuggestedIconParams): string => {
  const isUntouched = !currentIcon || currentIcon === lastSuggestion;

  if (!isUntouched) {
    return currentIcon;
  }

  return deriveIconUrlFromLink(newUrl, ownDomains);
};
